import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { ISelectOption } from '@components/common/Select/types';
import { addressToOptionTransformer } from '@pages/ApplicationFormPage/AddAddress/AddAddress';
import { useAddressesByPostcode } from '@queries/useAddressByPostcode/useAddressesByPostcode';
import { IAddressResponseDetails } from '@app-types/address';
import countriesList from '@pages/BtlApplicationFormPage/common/Address/countriesList';
import { BankDetailsAddressSelect } from '@pages/BankDetailsPage/components/fields/BankDetailsAddressSelect';
import { BankDetailsFieldsGroup } from '@pages/BankDetailsPage/components/layout/BankDetailsFieldsGroup';
import { BankDetailsSection } from '@pages/BankDetailsPage/components/layout/BankDetailsSection';
import { BankDetailsSelectField } from '@pages/BankDetailsPage/components/fields/BankDetailsSelectField';
import { BankDetailsTextField } from '@pages/BankDetailsPage/components/fields/BankDetailsTextField';
import type { BankDetailsFormValues } from './types';

function isValidPostcode(postcode: string): boolean {
  return /^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i.test(postcode);
}

function mapPostcodeAddressToForm(address: IAddressResponseDetails) {
  return {
    addressLine1: address.addressLine1,
    addressLine2: address.addressLine2 ?? '',
    town: address.townOrCity ?? '',
    country: address.country ?? '',
    postcode: address.postcode ?? '',
  };
}

interface BankAddressProps {
  disabled?: boolean;
}

export function BankAddress({ disabled = false }: BankAddressProps) {
  const intl = useIntl();
  const form = useFormContext<BankDetailsFormValues>();
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const isManual = useWatch({ name: 'bankAddress.isManual', control: form.control }) ?? false;
  const postcode = (useWatch({ name: 'bankAddress.postcode', control: form.control }) as string) || '';
  const selectedAddress = (useWatch({ name: 'bankAddress.selectedAddress', control: form.control }) as string) || '';

  const { data: addresses = [], refetch: fetchAddressesByPostcode, isFetching } = useAddressesByPostcode(postcode);
  const addressOptions = useMemo(() => addresses.map(addressToOptionTransformer), [addresses]);
  const [listSelection, setListSelection] = useState<string>('');
  const [hasSearchedAddresses, setHasSearchedAddresses] = useState(false);

  const countryOptions: ISelectOption[] = countriesList.map(country => ({
    label: country.label,
    value: country.value,
  }));

  const showManualFields = disabled || isManual;

  const setAddressFields = (body: ReturnType<typeof mapPostcodeAddressToForm>, selected: string) => {
    form.setValue('bankAddress.addressLine1', body.addressLine1, { shouldDirty: true });
    form.setValue('bankAddress.addressLine2', body.addressLine2, { shouldDirty: true });
    form.setValue('bankAddress.town', body.town, { shouldDirty: true });
    form.setValue('bankAddress.country', body.country, { shouldDirty: true });
    form.setValue('bankAddress.postcode', body.postcode, { shouldDirty: true });
    form.setValue('bankAddress.selectedAddress', selected, { shouldDirty: true, shouldValidate: true });
  };

  const setManualHandler = () => {
    form.setValue('bankAddress.isManual', true);
    setListSelection('');
    setHasSearchedAddresses(false);
    form.setValue('bankAddress.selectedAddress', '');
  };

  const setLookupHandler = () => {
    form.setValue('bankAddress.isManual', false);
    setHasSearchedAddresses(false);
    setListSelection('');
    form.setValue('bankAddress.selectedAddress', '');
  };

  const findUkAddressHandler = () => {
    fetchAddressesByPostcode().then(() => {
      setHasSearchedAddresses(true);
    });
  };

  useEffect(() => {
    if (disabled) {
      return;
    }

    if (selectedAddress && !listSelection) {
      try {
        const parsed = JSON.parse(selectedAddress);
        if (parsed.fullAddress) {
          setListSelection(parsed.fullAddress);
        }
      } catch {
        // ignore invalid stored value
      }
    }
  }, [disabled, selectedAddress, listSelection]);

  useEffect(() => {
    if (disabled || isManual) {
      return;
    }

    setHasSearchedAddresses(false);
    setListSelection('');
    form.setValue('bankAddress.selectedAddress', '');
  }, [postcode, isManual, form, disabled]);

  useEffect(() => {
    if (disabled || !hasSearchedAddresses || addressOptions.length === 0 || isManual || listSelection) {
      return;
    }

    const firstOptionValue = String(addressOptions[0].value);
    setListSelection(firstOptionValue);
    const matched = addresses.find(address => address.fullAddress === firstOptionValue);
    if (matched) {
      setAddressFields(mapPostcodeAddressToForm(matched), JSON.stringify(matched));
    }
  }, [hasSearchedAddresses, addressOptions, isManual, listSelection, addresses, form, disabled]);

  const selectPlaceholder = intl.formatMessage({ id: 'applicationDetails.bankDetails.selectAddress.placeholder' });

  return (
    <BankDetailsSection titleId='applicationDetails.bankDetails.bankAddress.section'>
      {showManualFields ? (
        <BankDetailsFieldsGroup>
          <div>
            <label className='mb-1 block text-sm text-[#000000E0]'>
              <span className='mr-1 text-[#ff4d4f]'>*</span>
              <FormattedMessage id='applicationDetails.bankDetails.address.label' />
            </label>
            <BankDetailsTextField
              name='bankAddress.addressLine1'
              disabled={disabled}
              rules={{ required: { value: true, message: requiredFieldErrorMessage } }}
              placeholder={intl.formatMessage({ id: 'applicationDetails.bankDetails.addressLine1.placeholder' })}
            />
          </div>
          <BankDetailsTextField
            name='bankAddress.addressLine2'
            disabled={disabled}
            placeholder={intl.formatMessage({ id: 'applicationDetails.bankDetails.addressLine2.placeholder' })}
          />
          <div>
            <label className='mb-1 block text-sm text-[#000000E0]'>
              <span className='mr-1 text-[#ff4d4f]'>*</span>
              <FormattedMessage id='applicationDetails.bankDetails.town.label' />
            </label>
            <BankDetailsTextField
              name='bankAddress.town'
              disabled={disabled}
              rules={{ required: { value: true, message: requiredFieldErrorMessage } }}
            />
          </div>
          <div>
            <label className='mb-1 block text-sm text-[#000000E0]'>
              <span className='mr-1 text-[#ff4d4f]'>*</span>
              <FormattedMessage id='applicationDetails.bankDetails.country.label' />
            </label>
            <BankDetailsSelectField
              name='bankAddress.country'
              placeholder={intl.formatMessage({ id: 'applicationDetails.bankDetails.country.placeholder' })}
              options={countryOptions}
              disabled={disabled}
              rules={{ required: { value: true, message: requiredFieldErrorMessage } }}
            />
          </div>
          <div>
            <label className='mb-1 block text-sm text-[#000000E0]'>
              <span className='mr-1 text-[#ff4d4f]'>*</span>
              <FormattedMessage id='applicationDetails.bankDetails.postcode.label' />
            </label>
            <BankDetailsTextField
              name='bankAddress.postcode'
              disabled={disabled}
              rules={{ required: { value: true, message: requiredFieldErrorMessage } }}
            />
          </div>
          {!disabled && (
            <button
              type='button'
              className='cursor-pointer border-0 bg-transparent p-0 text-left text-sm font-semibold text-green-700'
              onClick={setLookupHandler}
            >
              <FormattedMessage id='applicationDetails.bankDetails.searchForAddress' />
            </button>
          )}
        </BankDetailsFieldsGroup>
      ) : (
        <BankDetailsFieldsGroup>
          <div>
            <label className='mb-1 block text-sm text-[#000000E0]'>
              <span className='mr-1 text-[#ff4d4f]'>*</span>
              <FormattedMessage id='applicationDetails.bankDetails.postcode.label' />
            </label>
            <div className='flex items-center gap-3'>
              <BankDetailsTextField
                name='bankAddress.postcode'
                rules={{ required: { value: true, message: requiredFieldErrorMessage } }}
                narrow
              />
              <button
                type='button'
                disabled={!isValidPostcode(postcode) || isFetching}
                className='flex h-10 shrink-0 items-center whitespace-nowrap rounded-full border-0 bg-gray-100 px-6 text-sm font-normal text-black outline-hidden hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50'
                onClick={findUkAddressHandler}
              >
                <FormattedMessage
                  id={
                    isFetching
                      ? 'applicationDetails.bankDetails.findingAddress'
                      : 'applicationDetails.bankDetails.findUkAddress'
                  }
                />
              </button>
            </div>
          </div>
          {hasSearchedAddresses && (
            <div>
              <label className='mb-1 block text-sm text-[#000000E0]'>
                <FormattedMessage id='applicationDetails.bankDetails.selectAddress' />
              </label>
              <BankDetailsAddressSelect
                placeholder={selectPlaceholder}
                options={addressOptions}
                addresses={addresses}
                value={listSelection}
                disabled={addressOptions.length === 0}
                onSelect={(address, optionValue) => {
                  setListSelection(optionValue);
                  setAddressFields(
                    mapPostcodeAddressToForm(address as IAddressResponseDetails),
                    JSON.stringify(address),
                  );
                }}
              />
            </div>
          )}
          <button
            type='button'
            className='cursor-pointer border-0 bg-transparent p-0 text-left text-sm font-semibold text-green-700'
            onClick={setManualHandler}
          >
            <FormattedMessage id='applicationDetails.bankDetails.enterManually' />
          </button>
        </BankDetailsFieldsGroup>
      )}
    </BankDetailsSection>
  );
}
